import { DateTime } from "luxon";
import { LocationData, TideWindData } from "../datastructures";

export function friendlyWindUnitName(unit: string): string {
  switch (unit) {
    case "kts":
      return "Knopen";
    case "kmh":
      return "Kilometer per uur";
    case "ms":
      return "Meters per seconde";
    case "deg":
      return "Graden";
    default:
      throw new Error("Unknown unit " + unit);
  }
}

export function unitConversionFactor(destinationUnit: string): number {
  switch (destinationUnit) {
    case "kts":
      return 1.943844;
    case "kmh":
      return 3.6;
    case "ms":
      return 1;
    default:
      throw new Error("Unknown unit " + destinationUnit);
  }
}

export const convertWind = (
  windInMs: number,
  destinationUnit: string,
  fractionDigits: number = 0,
): number => {
  const factor = Math.pow(10, fractionDigits);
  return (
    Math.round(convertCurrent(windInMs, destinationUnit) * factor) / factor
  );
};

export const convertCurrent = (
  currentInMs: number,
  destinationUnit: string,
): number => currentInMs * unitConversionFactor(destinationUnit);

export function onlyUniqueValues(value: any, index: any, self: any[]) {
  return self.indexOf(value) === index;
}

export const localDateTimeFromEpoch = (validTime: number) =>
  DateTime.fromSeconds(validTime).setLocale("nl");

export const formatDate = (validTime: number, formatStr: string): string =>
  localDateTimeFromEpoch(validTime).toFormat(formatStr);

export const localDateNow = () => DateTime.now().setLocale("nl");

export const SC_BASE_PATH = "/sc";

const protocol = window.location.protocol;
const hostname = window.location.hostname;
const port = window.location.port;
const portStr = port === "" || port === "80" ? "" : ":" + port;
export const SC_BASE_URL = `${protocol}//${hostname}${portStr}${SC_BASE_PATH}`;

export const clone = (objectToClone: any): any =>
  JSON.parse(JSON.stringify(objectToClone));

export const portrait = (windowSize: {
  innerHeight: number;
  innerWidth: number;
}) => windowSize.innerHeight > windowSize.innerWidth;
export const nonMenuHeight = (windowSize: {
  innerHeight: number;
  innerWidth: number;
}) => windowSize.innerHeight - 110;
// export const graphHeight = (windowSize) =>
//   windowSize
//     ? portrait(windowSize)
//       ? nonMenuHeight(windowSize) / 2
//       : nonMenuHeight(windowSize)
//     : 100;

export const graphHeight = (windowSize: {
  innerHeight: number;
  innerWidth: number;
}) =>
  windowSize
    ? portrait(windowSize)
      ? windowSize.innerHeight / 2
      : nonMenuHeight(windowSize) > 300
      ? nonMenuHeight(windowSize)
      : windowSize.innerHeight
    : 100;

export function convertRemToPixels(rem: number): number {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

export function relNames(relDays: number) {
  switch (relDays) {
    case -2:
      return "daybeforeyesterday";
    case -1:
      return "yesterday";
    case 0:
      return "today";
    case 1:
      return "tomorrow";
    case 2:
      return "dayaftertomorrow";
    default:
      return new Error("Unable to make date diff relative");
  }
}

export function relDaysFromName(relativeDayName: string): number {
  switch (relativeDayName) {
    case "daybeforeyesterday":
      return -2;
    case "yesterday":
      return -1;
    case "today":
      return 0;
    case "tomorrow":
      return 1;
    case "dayaftertomorrow":
      return 2;
    default:
      throw new Error("Unsupported relativeDayName " + relativeDayName);
  }
}

export function relativeDateName(timestampSecs: number) {
  if (timestampSecs === 0) {
    return relNames(0);
  }
  const nowTimestampSecs = Math.round(Date.now() / 1000);
  const nowDate = localDateTimeFromEpoch(nowTimestampSecs);

  const eventDate = localDateTimeFromEpoch(timestampSecs);

  const weekdayDiff = eventDate.weekday - nowDate.weekday;
  const modedDiff =
    weekdayDiff < -4
      ? weekdayDiff + 7
      : weekdayDiff > 4
      ? weekdayDiff - 7
      : weekdayDiff;

  return relNames(modedDiff);
}

export const isToday = function (someDay: DateTime): boolean {
  return someDay.hasSame(DateTime.now(), "day");
};

export const formatDateFlexLength = function (date: DateTime): string {
  return isToday(date)
    ? date.toLocaleString({
        timeStyle: "short",
      })
    : date.toLocaleString({
        dateStyle: "full",
        timeStyle: "short",
      });
};

export function spotInfoLinkLocation(
  spotId: number,
  iconNr: number,
  timestampSecs: number,
): string {
  const sport = iconNr < 16 ? "kite" : "soar";
  const sportParam = iconNr ? "&sport=" + sport : "";
  const dayParam = "&day=" + relativeDateName(timestampSecs);
  return "/spotinfo?spot=" + spotId + sportParam + dayParam;
}

export function getSportFromHostname(): string {
  if (process.env.REACT_APP_VERSION === "DEVELOPMENT") {
    return process.env.REACT_APP_SPORT || "kite";
  }
  return window.location?.toString()?.toLowerCase()?.includes("soar")
    ? "soar"
    : "kite";
}

export function roundPlaces(value: number, places: number): number {
  return +(Math.round(Number(value + "e+" + places)) + "e-" + places);
}

export function roundTowardZero(num: number): number {
  return num >= 0 ? Math.floor(num) : Math.ceil(num);
}

export const locationLabel = (location: LocationData | undefined): string => {
  if (location === undefined) {
    return "";
  }
  return locationLabelFromNameAndSource(
    location.location_name,
    location.source,
  );
};

export const locationLabelFromNameAndSource = (
  location_name: string,
  location_source: string,
): string => {
  const sourcesImpliedByLocationName = [
    "NKV",
    "KZVS",
    "WOLDERWIJD",
    "Meteotoren",
  ];
  if (sourcesImpliedByLocationName.includes(location_source)) {
    return location_name;
  } else {
    return location_name + " (" + location_source + ")";
  }
};

// Smileys per halve knoop stroomsnelheid
export const tideSmileys: Record<string, string> = {
  "-6": "⚠️⚠️🏴‍☠️",
  "-5": "⚠️⚠️⚠️",
  "-4": "😭⚠️",
  "-3": "😫",
  "-2": "😟",
  "-1": "😕",
  "0": "😶",
  "1": "🙂",
  "2": "😀",
  "3": "😁🤙",
  "4": "😁🤙⚠️",
  "5": "😬🤙⚠️",
  "6": "😬⚠️⚠️",
};

export interface TideCurrentInfo {
  upSpeedMsec: number;
  smiley: string;
  currDir: string;
  currColor: string;
  currWebkitColor: string;
  colorCss: React.CSSProperties;
  waterLevel: number;
  displayTideTime: string;
  nextTideInfo: TideWindData | undefined;
}

export function getTideCurrentInfo(
  tideWindData: TideWindData[],
  etimeToShow: number,
  spotId: number,
): TideCurrentInfo | undefined {
  // Filter for this spot (spotId is now mandatory)
  let spotTideData = tideWindData.filter((row) => row.spot_id === spotId);
  let nextTideInfo: TideWindData | undefined;
  if (etimeToShow === 0) {
    const tideTimes = spotTideData.map((x) => x.validtime);
    const futureTideTimes = tideTimes
      .filter(Boolean)
      .map((x) => Number(x))
      .filter((x) => x > Date.now() / 1000);
    const nextTideTime = Math.min(...futureTideTimes);
    nextTideInfo = spotTideData.find((x) => x.validtime === nextTideTime);
  } else {
    nextTideInfo = spotTideData.find((x) => x.validtime === etimeToShow);
  }
  if (!nextTideInfo) return undefined;
  const upSpeedMsec = nextTideInfo.upwind_cur_speed;
  const flowsUp = upSpeedMsec > 0;
  const intensity = Math.round(
    Math.min(1, Math.abs(upSpeedMsec)) * 255,
  ).toString();
  const green = flowsUp ? intensity : "0";
  const red = flowsUp ? "0" : intensity;
  const currColor = `rgb(${red}, ${green}, 0, 1)`;
  const currWebkitColor = `rgba(${red}, ${green}, 0, 1)`;
  const colorCss = {
    color: currColor,
    WebkitTextFillColor: currWebkitColor,
    WebkitOpacity: 1,
  };
  const smiley =
    tideSmileys[
      roundTowardZero(convertCurrent(upSpeedMsec, "kts") * 2).toString()
    ];
  const currDir = upSpeedMsec >= 0 ? "tegen de wind in" : "met de wind mee";
  const waterLevel = nextTideInfo.sep;
  const displayTideTime = formatDateFlexLength(
    localDateTimeFromEpoch(nextTideInfo.validtime),
  );
  return {
    upSpeedMsec,
    smiley,
    currDir,
    currColor,
    currWebkitColor,
    colorCss,
    waterLevel,
    displayTideTime,
    nextTideInfo,
  };
}
